import { createPatchData, useRolodexEdit } from 'features/fde'
import {
  LIBRA_WORKFLOW_ENTRY_TYPE,
  mapEntryToLibraWorkflow,
} from 'features/libra/workflow/workflowStepsApi'
import { useGeckoNotifications } from 'features/notifications'
import { useRolodexEntries } from 'features/rolodex'
import first from 'lodash/first'
import isNil from 'lodash/isNil'
import { useCallback, useMemo } from 'react'
import {
  GeckoEntry,
  RolodexEntryPatchJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'
import z from 'zod'

export const LIBRA_TICKET_ENTRY_TYPE = 'libra-ticket' as RolodexEntryType
export const LIBRA_TICKET_GROUP_ENTRY_TYPE =
  'libra-ticket-group' as RolodexEntryType

const LibraTicketGroupDataSchema = z.object({
  title: z.string().default('Untitled Group'),
})
type LibraTicketGroupData = z.infer<typeof LibraTicketGroupDataSchema>

export type LibraTicketGroup = LibraTicketGroupData & {
  id: string
  tags: RolodexEntryTagsType
}

export interface LibraTicket {
  id: string
  workflowId: string
  title: string
  props: Record<string, unknown>
  stepId: string
  workflowComplete: boolean
  createdAt: string
  updatedAt: string
  tags: RolodexEntryTagsType
}

const createLibraTicketDataSchema = (defaultStepId: string) =>
  z.object({
    title: z.string(),
    props: z.record(z.string(), z.unknown()).default({}),
    stepId: z.string().default(defaultStepId),
    workflowComplete: z.boolean().optional().default(false),
  })

const mapEntryToLibraTicket = (
  entry: GeckoEntry,
  parseTicket: (data: GeckoEntry['data']) => {
    title: string
    props: Record<string, unknown>
    stepId: string
    workflowComplete: boolean
  }
): LibraTicket | undefined => {
  const workflowId = entry.tags[LIBRA_WORKFLOW_ENTRY_TYPE]
  if (!workflowId) {
    return undefined
  }

  const parsedData = parseTicket({ ...entry.data })

  return {
    id: entry.id,
    title: parsedData.title,
    props: parsedData.props,
    stepId: parsedData.stepId,
    workflowComplete: parsedData.workflowComplete,
    createdAt: entry.created_at ?? '',
    updatedAt: entry.updated_at ?? '',
    workflowId,
    tags: entry.tags,
  }
}

const mapEntryToLibraTicketGroup = (
  entry: GeckoEntry,
  parseGroup: (data: GeckoEntry['data']) => {
    title: string
  }
): LibraTicketGroup => {
  const parsedData = parseGroup({ ...entry.data })

  return {
    id: entry.id,
    title: parsedData.title,
    tags: entry.tags,
  }
}

export type UpdateTicketArgs = {
  ticketId: string
  stepId?: string
  props?: Record<string, unknown>
  workflowComplete?: boolean
}

export const useLibraTickets = (
  tags: RolodexEntryTagsType
): {
  tickets: ReadonlyArray<LibraTicket>
  groups: ReadonlyArray<LibraTicketGroup>
  ticketGroups: ReadonlyMap<string, LibraTicketGroup>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
  doUpdateTicket: (arg: UpdateTicketArgs) => Promise<void>
} => {
  const queryBody = useMemo(() => {
    return {
      type: LIBRA_TICKET_ENTRY_TYPE,
      links: [LIBRA_TICKET_GROUP_ENTRY_TYPE, LIBRA_WORKFLOW_ENTRY_TYPE],
      tags,
    }
  }, [tags])

  const { entries, isLoading, hasEverLoaded, forceRefresh } =
    useRolodexEntries(queryBody)

  const workflowIdToDefaultStepId = useMemo<ReadonlyMap<string, string>>(() => {
    const workflows = (entries ?? [])
      .filter((entry) => entry.type === LIBRA_WORKFLOW_ENTRY_TYPE)
      .map(mapEntryToLibraWorkflow)

    return workflows.reduce((acc, workflow) => {
      const firstStep = first(workflow.steps)
      return firstStep ? new Map(acc).set(workflow.id, firstStep.id) : acc
    }, new Map<string, string>())
  }, [entries])

  const { ticketEntries, groupEntries } = useMemo(
    () => ({
      ticketEntries: (entries ?? []).filter(
        (entry) => entry.type === LIBRA_TICKET_ENTRY_TYPE
      ),
      groupEntries: (entries ?? []).filter(
        (entry) => entry.type === LIBRA_TICKET_GROUP_ENTRY_TYPE
      ),
    }),
    [entries]
  )
  const { errorNotification } = useGeckoNotifications()
  const { rolodexPatch } = useRolodexEdit()

  const getTicketSchema = useCallback(
    (ticket: GeckoEntry) => {
      const workflowId = ticket.tags[LIBRA_WORKFLOW_ENTRY_TYPE]
      const result = workflowId
        ? createLibraTicketDataSchema(
            workflowIdToDefaultStepId.get(workflowId) ?? ''
          )
        : undefined
      if (!result) {
        errorNotification(
          `Unable to construct ticket schema for ticket ${ticket.id}`
        )
        console.error(
          `Unable to construct ticket schema for ticket ${ticket.id}`
        )
        return undefined
      }
      return result
    },
    [workflowIdToDefaultStepId, errorNotification]
  )

  const doUpdateTicket = useCallback(
    async ({ ticketId, stepId, props, workflowComplete }: UpdateTicketArgs) => {
      const ticket = ticketEntries?.find((entry) => entry.id === ticketId)
      if (!ticket) {
        console.warn(`Unknown ticket id: ${ticketId}`)
        return
      }
      const ticketSchema = getTicketSchema(ticket)
      if (!ticketSchema) {
        return
      }
      const ticketData = ticketSchema.parse(ticket.data)
      const {
        props: ticketProps,
        stepId: ticketStepId,
        workflowComplete: ticketWorkflowComplete,
      } = ticketData

      const patchProps = props
        ? createPatchData(ticketId, { props: ticketProps }, { props })
        : undefined

      const patchStepId = stepId
        ? createPatchData(ticketId, { stepId: ticketStepId }, { stepId })
        : undefined

      const patchWorkflowComplete = !isNil(workflowComplete)
        ? createPatchData(
            ticketId,
            { workflowComplete: ticketWorkflowComplete },
            { workflowComplete }
          )
        : undefined

      if (!patchProps && !patchStepId && !patchWorkflowComplete) {
        console.warn(`No changes to ticket ${ticketId}`)
        return
      }
      await rolodexPatch(
        [patchProps, patchStepId, patchWorkflowComplete].filter(
          (patch): patch is RolodexEntryPatchJson => patch !== undefined
        )
      )
      await forceRefresh()
    },
    [ticketEntries, getTicketSchema, rolodexPatch, forceRefresh]
  )
  return useMemo(() => {
    const sortedTickets =
      ticketEntries
        .map((entry) => {
          const schema = getTicketSchema(entry)
          if (!schema) return undefined
          try {
            return mapEntryToLibraTicket(entry, (data) => schema.parse(data))
          } catch (error) {
            console.error(
              `Failed to parse ticket data for entry ${entry.id}:`,
              error,
              entry
            )
            return undefined
          }
        })
        .filter((ticket): ticket is LibraTicket => ticket !== undefined)
        .toSorted((a, b) => {
          if (a.workflowComplete !== b.workflowComplete) {
            return a.workflowComplete ? 1 : -1
          }
          return b.createdAt.localeCompare(a.createdAt)
        }) ?? []

    const groups = groupEntries.map((entry) =>
      mapEntryToLibraTicketGroup(entry, (data) =>
        LibraTicketGroupDataSchema.parse(data)
      )
    )

    const groupsMap = new Map(groups.map((group) => [group.id, group]))

    return {
      tickets: sortedTickets,
      ticketGroups: groupsMap,
      groups,
      isLoading,
      hasEverLoaded,
      forceRefresh,
      doUpdateTicket,
    }
  }, [
    doUpdateTicket,
    forceRefresh,
    getTicketSchema,
    groupEntries,
    hasEverLoaded,
    isLoading,
    ticketEntries,
  ])
}
